import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup } from "react-leaflet";
import api from "../lib/api";
import { X, WarningOctagon } from "@phosphor-icons/react";

// TripGpsTrailModal — shows the GPS trail during the trip's execution window and compares:
//   - actual km driven (sum of haversines between snapshots)
//   - planned km (straight-line first-to-last snapshot — best-effort baseline)
// Flags a deviation when actual > planned × 1.3.
export default function TripGpsTrailModal({ activityId, onClose }) {
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!activityId) return;
    setLoading(true);
    setErr("");
    api.get(`/activities/${activityId}/gps-trail`)
      .then(({ data }) => setTrail(data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [activityId]);

  const snaps = trail?.snapshots || [];
  const path = snaps.map((s) => [s.lat, s.lng]);
  const center = path.length ? path[Math.floor(path.length / 2)] : [6, 0.5];
  const flag = trail?.deviation_flag;

  return (
    <div className="fixed inset-0 z-[1200] bg-slate-900/60 grid place-items-center p-4" onClick={onClose} data-testid="trip-gps-trail-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col" style={{ maxHeight: "92vh" }}>
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Trip GPS trail</div>
            <h3 className="font-heading font-black text-xl tracking-tight" data-testid="trip-trail-title">{trail?.vehicle_number || "…"}</h3>
            <div className="text-[11px] text-slate-500 mt-0.5 font-mono-data tabular">
              {trail?.trip_start?.slice(0, 16).replace("T", " ")} → {trail?.trip_end?.slice(0, 16).replace("T", " ")}
            </div>
          </div>
          <button onClick={onClose} data-testid="trip-trail-close" className="text-slate-400 hover:text-ps-deep p-1"><X size={16} weight="bold" /></button>
        </div>
        <div className="relative" style={{ height: 480 }}>
          {loading && <div className="absolute inset-0 grid place-items-center text-slate-500 text-xs z-[1000] pointer-events-none">Loading…</div>}
          {err && <div className="absolute top-2 left-2 right-2 z-[1000] bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded">{err}</div>}
          {!loading && !err && snaps.length === 0 && (
            <div className="absolute inset-0 grid place-items-center text-slate-500 text-sm z-[1000] pointer-events-none">
              No GPS points during this trip window.
            </div>
          )}
          <MapContainer center={center} zoom={9} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true} attributionControl={true}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
            {/* Planned route = straight line from first to last GPS point */}
            {path.length >= 2 && (
              <Polyline positions={[path[0], path[path.length - 1]]} pathOptions={{ color: "#94a3b8", weight: 3, dashArray: "8 6", opacity: 0.7 }} />
            )}
            {/* Actual route */}
            {path.length > 1 && <Polyline positions={path} pathOptions={{ color: flag ? "#dc2626" : "#0ea5e9", weight: 4, opacity: 0.9 }} />}
            {snaps.map((s, i) => (i === 0 || i === snaps.length - 1) && (
              <CircleMarker key={s.id} center={[s.lat, s.lng]} radius={i === 0 ? 8 : 10}
                pathOptions={{ color: i === 0 ? "#10b981" : "#ef4444", fillColor: i === 0 ? "#10b981" : "#ef4444", fillOpacity: 0.9 }}>
                <Popup>{i === 0 ? "Start" : "End"} · {s.ts.slice(0, 19).replace("T", " ")}<br />{s.address || "—"}</Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-4 flex-wrap">
          <Stat label="Snapshots" value={snaps.length} testid="trip-trail-snaps" />
          <Stat label="Actual km" value={trail?.distance_km_actual ?? "—"} testid="trip-trail-actual" />
          <Stat label="Planned km" value={trail?.distance_km_planned ?? "—"} hint="straight-line baseline" testid="trip-trail-planned" />
          {trail?.deviation_pct != null && (
            <Stat
              label="Deviation"
              value={`${trail.deviation_pct > 0 ? "+" : ""}${trail.deviation_pct}%`}
              color={flag ? "text-ps-red" : "text-slate-700"}
              testid="trip-trail-deviation"
            />
          )}
          {flag && (
            <div className="ml-auto flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-700 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wide" data-testid="trip-trail-flag">
              <WarningOctagon size={12} weight="fill" /> big detour
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, hint, color, testid }) {
  return (
    <div className="text-xs" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500">{label}{hint && <span className="text-slate-400 normal-case tracking-normal ml-1">({hint})</span>}</div>
      <div className={`font-mono-data tabular text-sm font-bold ${color || "text-slate-800"}`}>{value}</div>
    </div>
  );
}
